'use client'

// We cannot useState or useRef in a server component, which is why we are
// extracting this part out into its own file with 'use client' on top
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React, { useEffect, useState, Suspense } from 'react'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { UserContextProvider } from '@/lib/contexts/UserContext'
import Event from '@bugsnag/core/types/event'
import { FeedbackDialog } from '@/components/feedback-dialog'
import { EventEmitterProvider, useEventEmitterContext } from '@/lib/contexts/EventEmitterContext'
import { useLocalStorageState } from 'ahooks'
import SetupSearchCallback from '@/components/setup-search-callback'

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
        // Don't refetch on window focus
        refetchOnWindowFocus: false,
        // Do not retry on error
        retry: false,
        // Throw on error by default
        throwOnError: true,
      },
      mutations: {
        // Always disable retries on
        retry: false,
        // Throw on error by default
        throwOnError: true,
      },
    },
  })
}

let browserQueryClient: QueryClient | undefined = undefined

function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return makeQueryClient()
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient()
    return browserQueryClient
  }
}

function startBugsnag() {
  // If we're running in browser and Bugsnag is configured, use it to report errors.
  if (typeof window !== 'undefined' && !!process.env.NEXT_PUBLIC_BUGSNAG_API_KEY) {
    const Bugsnag = require('@bugsnag/js')

    if (!Bugsnag._client) {
      console.debug('Starting Bugsnag...')

      Bugsnag.start({
        apiKey: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY,
        releaseStage: process.env.NODE_ENV,
        enabledReleaseStages: ['production'],
        endpoints: {
          notify: 'https://bs-notify.productioncrate.com',
          sessions: 'https://bs-sessions.productioncrate.com',
        },
        onError: (event: Event) => {
          console.error('Bugsnag error:', event)
        },
      })
    }
  }
}

/**
 * Feedback dialog handler.
 * This component listens to app-wide events and shows the feedback dialog when needed.
 * Also, when query param feedback=new-design is present, it will show the feedback dialog.
 */
function FeedbackDialogHandler() {
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false)
  const appEvent$ = useEventEmitterContext()
  const [lastDownloadedAt, setLastDownloadedAt] = useLocalStorageState('crate.lastDownloadedAt')

  appEvent$.useSubscription((evt) => {
    if (evt.type === 'asset:download') {
      if (!lastDownloadedAt) {
        setTimeout(() => setFeedbackDialogOpen(true), 3000)
      }
      setLastDownloadedAt(Date.now())
    }
  })

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('feedback') === 'new-design') {
      setFeedbackDialogOpen(true)
    }
  }, [])

  return <FeedbackDialog open={feedbackDialogOpen} onOpenChange={setFeedbackDialogOpen} />
}

export default function Providers({ children }: { children: React.ReactNode }) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient()

  // Start Bugsnag on mount
  useEffect(() => {
    startBugsnag()
  }, [])

  return (
    <UserContextProvider>
      <QueryClientProvider client={queryClient}>
        <EventEmitterProvider>
          {children}
          <FeedbackDialogHandler />
          <Suspense fallback={null}>
            <SetupSearchCallback />
          </Suspense>
        </EventEmitterProvider>
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      </QueryClientProvider>
    </UserContextProvider>
  )
}
